import { mapKeys } from 'es-toolkit';
import { type ComponentType, type ReactNode, useCallback, useMemo } from 'react';
import { z } from 'zod';
import { LinkPreview } from '@/components/shared/link-preview/LinkPreview';
import { parseLinkMetadata } from '@/components/shared/link-preview/schemas';
import type { LinkMetadata as LinkPreviewMetadata } from '@/components/shared/link-preview/types';
import { canRenderAnotherInlineTweet } from '@/components/tweet/inline-tweet-policy';
import TweetMedia from '@/components/tweet/media/TweetMedia';
import { TweetMediaStatic } from '@/components/tweet/TweetMediaStatic';
import { ExpandableTweetText } from '@/domains/timeline/components/ExpandableTweetText';
import type { LinkMetadata as FetchLinkMetadata } from '@/lib/fetch-metadata';
import { getBrowserTrpcClient } from '@/lib/trpc/browser-client';
import type { TweetMedia as ParsedTweetMedia } from '@/lib/tweet-parser';
import { canonicalKeyForComparison } from '@/lib/url/normalize';
import { toLinkMetadataRecord } from '@/lib/url-metadata/normalize';
import { cn } from '@/lib/utils';
import { normalizeTweetMediaItems, transformProcessedMediaToUI } from '@/lib/validation/transformers/media-ui';
import type { MediaItem, TweetEntities } from '@/types/tweet';
import { TweetEntitiesSchema } from '@/types/tweet';
import type { MediaMetadataEntry } from '@/types/tweet/media';
import { MediaMetadataEntrySchema } from '@/types/tweet/media';

const noopVisibilityChange = (_visible: boolean): void => {
  /* no-op */
};

const noopMediaToggle = (_active: boolean): void => {
  /* no-op */
};

interface TweetBodyProperties {
  cardUrls?: Array<{ url: string; expandedUrl?: string; displayUrl?: string }> | undefined;
  className?: string | undefined;
  compact?: boolean | undefined;
  defaultExpanded?: boolean | undefined;
  disableLinkPreviews?: boolean | undefined;
  footerSlot?: ReactNode | undefined;
  highlightCardUrl?: string | undefined;
  inlineTweetComponent?:
    | ComponentType<{ tweetId: string; fallbackUrl?: string; visitedIds?: ReadonlySet<string> }>
    | undefined;
  inlineTweetUrls?: Array<{ tweetId: string; url: string }> | undefined;
  inlineTweetVisitedIds?: ReadonlySet<string> | undefined;
  longFormHint?: boolean | undefined;
  mediaItems?: MediaItem[] | undefined;
  mediaMetadata?: MediaMetadataEntry[] | undefined;
  mediaRenderMode?: 'interactive' | 'static' | undefined;
  mentionProfiles?:
    | Map<string, { username: string; name?: string | null; profileImageUrl?: string | null }>
    | undefined;
  metadata?: Record<string, FetchLinkMetadata> | undefined;
  onFullTweetLoaded?:
    | ((t: {
        text: string;
        entities?: TweetEntities;
        mediaItems?: ParsedTweetMedia[];
        metadata?: Record<string, FetchLinkMetadata>;
        mediaMetadata?: MediaMetadataEntry[];
      }) => void)
    | undefined; // allow parent to sync parsed state
  onMediaViewerToggle?: ((isActive: boolean) => void) | undefined;
  onShowMoreVisibilityChange?: ((visible: boolean) => void) | undefined;
  parsedHtml: string;
  quotedTweetId?: string | undefined;
  renderQuotedFallback?: boolean | undefined;
  // Optional tweet context for on-demand long-form fetch
  tweetId?: string | undefined;
}

const normalizeTweetEntities = (value: unknown): TweetEntities | undefined => {
  if (value === undefined) {
    return;
  }
  const result = TweetEntitiesSchema.safeParse(value);
  return result.success ? result.data : undefined;
};

const toLinkPreviewMetadataValue = (value: FetchLinkMetadata | null | undefined): LinkPreviewMetadata | null => {
  if (!value) {
    return null;
  }
  return parseLinkMetadata(value) ?? value;
};

const FullTweetResponseSchema = z
  .object({
    text: z.string().optional(),
    entities: z.unknown().optional(),
    mediaItems: z.unknown().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    mediaMetadata: z.array(MediaMetadataEntrySchema).optional(),
  })
  .passthrough();

const extractMediaMetadata = (value: unknown): MediaMetadataEntry[] | undefined => {
  if (!value) {
    return;
  }
  const parsed = z.array(MediaMetadataEntrySchema).safeParse(value);
  return parsed.success ? parsed.data : undefined;
};

export function TweetBody({
  parsedHtml,
  mediaItems,
  cardUrls = [],
  inlineTweetUrls = [],
  quotedTweetId,
  metadata,
  mediaMetadata,
  compact = false,
  className = '',
  tweetId,
  longFormHint = false,
  onFullTweetLoaded,
  disableLinkPreviews = false,
  highlightCardUrl,
  onMediaViewerToggle,
  renderQuotedFallback = true,
  inlineTweetComponent,
  inlineTweetVisitedIds,
  onShowMoreVisibilityChange,
  defaultExpanded = false,
  footerSlot,
  mediaRenderMode = 'interactive',
  mentionProfiles,
}: TweetBodyProperties) {
  const InlineTweetComponent = inlineTweetComponent;
  // Filter out quoted tweet from inline tweets to avoid duplication
  const filteredInlineTweetUrls = quotedTweetId
    ? inlineTweetUrls.filter((url) => url.tweetId !== quotedTweetId)
    : inlineTweetUrls;
  const allowAnotherInlineEmbed = canRenderAnotherInlineTweet(inlineTweetVisitedIds);

  const linkPreviewEntries = useMemo(() => {
    const counts = new Map<string, number>();
    return cardUrls.map(({ expandedUrl, url }) => {
      const resolvedUrl = expandedUrl ?? url;
      const occurrence = counts.get(resolvedUrl) ?? 0;
      counts.set(resolvedUrl, occurrence + 1);
      const key = occurrence === 0 ? `link-${resolvedUrl}` : `link-${resolvedUrl}-${occurrence}`;
      return { key, resolvedUrl };
    });
  }, [cardUrls]);
  const highlightKey = useMemo(() => canonicalKeyForComparison(highlightCardUrl), [highlightCardUrl]);
  const metadataByCanonicalKey = useMemo(() => {
    if (!metadata) {
      return null;
    }
    const normalized = mapKeys(metadata, (_value, url) => canonicalKeyForComparison(url) ?? url);
    const map = new Map<string, LinkPreviewMetadata>();
    for (const [key, value] of Object.entries(normalized)) {
      if (!key || map.has(key)) {
        continue;
      }
      const parsed = toLinkPreviewMetadataValue(value);
      if (!parsed) {
        continue;
      }
      map.set(key, parsed);
    }
    return map;
  }, [metadata]);

  const trpcClient = getBrowserTrpcClient();

  const loadFullTweet = useCallback(
    async (id: string) => {
      const full = await trpcClient.tweets.getTweet.query({
        tweetId: id,
      });
      const parsed = FullTweetResponseSchema.safeParse(full);
      const record = parsed.success ? parsed.data : {};
      const normalizedEntities = normalizeTweetEntities(record.entities);
      const normalizedMediaItems = normalizeTweetMediaItems(record.mediaItems);
      const normalizedMetadata = toLinkMetadataRecord(record.metadata);
      const normalizedMediaMetadata = extractMediaMetadata(record.mediaMetadata) ?? [];
      const normalized: {
        text: string;
        entities?: TweetEntities;
        mediaItems?: ParsedTweetMedia[];
        metadata?: Record<string, FetchLinkMetadata>;
        mediaMetadata?: MediaMetadataEntry[];
      } = {
        text: typeof record.text === 'string' ? record.text : '',
      };
      if (normalizedEntities) {
        normalized.entities = normalizedEntities;
      }
      if (normalizedMediaItems) {
        normalized.mediaItems = normalizedMediaItems;
      }
      if (normalizedMetadata) {
        normalized.metadata = normalizedMetadata;
      }
      if (normalizedMediaMetadata.length > 0) {
        normalized.mediaMetadata = normalizedMediaMetadata;
      }

      onFullTweetLoaded?.(normalized);
      return normalized;
    },
    [onFullTweetLoaded, trpcClient]
  );

  // Single interactive area for conversation open; avoid nesting <button>
  // Provide an expand hook that fetches full tweet content on-demand
  const handleExpandFetch = useCallback(async () => {
    if (tweetId === undefined) {
      return;
    }
    try {
      await loadFullTweet(tweetId);
    } catch {
      // Ignore errors; user still sees truncated content expanded
    }
  }, [loadFullTweet, tweetId]);

  const normalizedMediaItems = useMemo(
    () => transformProcessedMediaToUI(mediaItems, mediaMetadata),
    [mediaItems, mediaMetadata]
  );

  const handleShowMoreVisibilityChange = onShowMoreVisibilityChange ?? noopVisibilityChange;
  const handleMediaViewerToggle = onMediaViewerToggle ?? noopMediaToggle;

  return (
    <div className={cn('w-full max-w-full min-w-0 overflow-x-hidden', className)} data-skip-link-enhancer>
      <ExpandableTweetText
        html={parsedHtml}
        className={cn(
          'mt-1 whitespace-pre-wrap break-words [word-break:break-word] [overflow-wrap:anywhere] text-foreground',
          compact ? 'text-sm leading-relaxed' : 'text-[14px] sm:text-[15px] leading-5 sm:leading-6'
        )}
        // Opt-in universally so any surface using TweetBody gets the shared "Show more" overflow clamp
        collapsible
        forceShowButton={longFormHint}
        onExpand={handleExpandFetch}
        onShowMoreVisibilityChange={handleShowMoreVisibilityChange}
        defaultExpanded={defaultExpanded}
        footerSlot={footerSlot}
        {...(mentionProfiles ? { mentionProfiles } : {})}
      />

      {/* Media attachments */}
      {normalizedMediaItems.length > 0 &&
        (mediaRenderMode === 'static' ? (
          <TweetMediaStatic mediaItems={normalizedMediaItems} />
        ) : (
          <TweetMedia mediaItems={normalizedMediaItems} onViewerStateChange={handleMediaViewerToggle} />
        ))}

      {/* Quote tweet (from referencedTweets) */}
      {quotedTweetId &&
        renderQuotedFallback &&
        InlineTweetComponent &&
        (!allowAnotherInlineEmbed || inlineTweetVisitedIds?.has(quotedTweetId) ? (
          // Once an id has been seen, render a simple link to avoid looping back through InlineTweet.
          <a
            key={quotedTweetId}
            href={`https://twitter.com/i/status/${quotedTweetId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-sm text-primary hover:underline"
          >
            View quoted tweet
          </a>
        ) : (
          <InlineTweetComponent
            key={quotedTweetId}
            tweetId={quotedTweetId}
            fallbackUrl={`https://twitter.com/i/status/${quotedTweetId}`}
            {...(inlineTweetVisitedIds ? { visitedIds: inlineTweetVisitedIds } : {})}
          />
        ))}

      {/* Inline tweets (from URLs) - excluding quoted tweet */}
      {InlineTweetComponent &&
        filteredInlineTweetUrls.map((tweetUrl) => {
          if (inlineTweetVisitedIds?.has(tweetUrl.tweetId)) {
            return null;
          }

          if (!allowAnotherInlineEmbed) {
            return null;
          }

          return (
            <InlineTweetComponent
              key={`inline-${tweetUrl.tweetId}-${tweetUrl.url}`}
              tweetId={tweetUrl.tweetId}
              fallbackUrl={tweetUrl.url}
              // Pass the visited set down so grandchildren can short-circuit as well.
              {...(inlineTweetVisitedIds ? { visitedIds: inlineTweetVisitedIds } : {})}
            />
          );
        })}

      {/* URL cards with link previews */}
      {disableLinkPreviews
        ? null
        : linkPreviewEntries.map(({ key, resolvedUrl }) => {
            const comparisonKey = canonicalKeyForComparison(resolvedUrl);
            const metadataFromMap =
              metadataByCanonicalKey && comparisonKey ? metadataByCanonicalKey.get(comparisonKey) : undefined;
            const fallbackMetadata = toLinkPreviewMetadataValue(metadata?.[resolvedUrl]);
            const urlMetadata = metadataFromMap ?? fallbackMetadata ?? undefined;
            const isHighlighted = Boolean(highlightKey && comparisonKey && highlightKey === comparisonKey);

            return (
              <LinkPreview
                key={key}
                url={resolvedUrl}
                metadata={urlMetadata}
                className={cn(
                  isHighlighted &&
                    'rounded-2xl ring-2 ring-primary/70 ring-offset-2 ring-offset-background shadow-lg shadow-primary/20 transition-shadow'
                )}
              />
            );
          })}
    </div>
  );
}
